import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Badge,
    Row,
    Col,
    ButtonGroup,
    Accordion,
    Table,
    Tabs,
    Tab,
} from 'react-bootstrap';
import {
    FaPlay,
    FaDatabase,
    FaCog,
    FaCheckCircle,
    FaTimesCircle,
    FaChartLine,
    FaBug,
    FaClock,
    FaExclamationTriangle,
    FaCalculator,
    FaSave,
    FaTerminal,
    FaListUl,
} from 'react-icons/fa';

const API_URL = 'http://localhost:8001/api';

interface Instance {
    id: number;
    name: string;
    status: string;
    broker: { id: number; name: string };
    broker_account: { id: number; account_id: string; account_type: string };
    linked_algos: LinkedAlgo[];
}

interface LinkedAlgo {
    id: number;
    name: string;
    description: string;
    parent_algo_id: number;
}

interface LinkedAlgoConfig {
    id: number;
    name: string;
    description: string;
    parameters: any;
    symbols: Array<{ symbol: string; assetClass: string }>;
    asset_classes: Array<{ id: number; name: string }>;
}

interface DebugStep {
    step: number;
    label: string;
    function: string;
    timestamp: string;
    data?: any;
    error?: string;
}

interface TradeResult {
    success: boolean;
    message: string;
    trade_details?: any;
    error?: string;
    broker_response?: any;
    debug_log: DebugStep[];
    quantity_trace?: {
        function?: string;
        lot_mode?: string;
        balance?: string;
        risk_percentage?: string;
        money_at_risk?: string;
        point_size?: string;
        point_value?: string;
        sl_distance?: string;
        risk_points?: string;
        denominator?: string;
        calculated_lots?: string;
        is_fixed?: boolean;
    };
    saved_trade?: any;
}

// ─── Collapsible JSON Viewer ───────────────────────────────────────────

const JsonBlock: React.FC<{ data: any; label?: string; defaultOpen?: boolean }> = ({
    data,
    label,
    defaultOpen = false,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    if (data === null || data === undefined) return null;

    return (
        <div style={{ marginBottom: '0.5rem' }}>
            {label && (
                <div
                    style={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: '#6c63ff',
                        userSelect: 'none',
                    }}
                    onClick={() => setOpen(!open)}
                >
                    {open ? '▾' : '▸'} {label}
                </div>
            )}
            {(open || !label) && (
                <pre
                    style={{
                        background: '#1e1e2e',
                        color: '#a6e3a1',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        maxHeight: '400px',
                        overflow: 'auto',
                        margin: label ? '0.25rem 0 0 1rem' : 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
                </pre>
            )}
        </div>
    );
};

// ─── Main Component ────────────────────────────────────────────────────

const TestTrade: React.FC = () => {
    const [instances, setInstances] = useState<Instance[]>([]);
    const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
    const [selectedLinkedAlgo, setSelectedLinkedAlgo] = useState<LinkedAlgo | null>(null);
    const [algoConfig, setAlgoConfig] = useState<LinkedAlgoConfig | null>(null);
    const [selectedSymbol, setSelectedSymbol] = useState<string>('');
    const [action, setAction] = useState<'BUY' | 'SELL'>('BUY');
    const [overrideQuantity, setOverrideQuantity] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [result, setResult] = useState<TradeResult | null>(null);

    useEffect(() => {
        loadInstances();
    }, []);

    useEffect(() => {
        if (selectedLinkedAlgo) {
            loadAlgoConfig(selectedLinkedAlgo.id);
        } else {
            setAlgoConfig(null);
            setSelectedSymbol('');
        }
    }, [selectedLinkedAlgo]);

    const loadInstances = async () => {
        try {
            const response = await axios.get(`${API_URL}/test-trade/instances`);
            setInstances(response.data.instances || []);
        } catch (error: any) {
            console.error('Error loading instances:', error);
        }
    };

    const loadAlgoConfig = async (linkedAlgoId: number) => {
        try {
            setLoadingConfig(true);
            const response = await axios.get(`${API_URL}/test-trade/linked-algos/${linkedAlgoId}`);
            setAlgoConfig(response.data);
            if (response.data.symbols && response.data.symbols.length > 0) {
                setSelectedSymbol(response.data.symbols[0].symbol);
            }
        } catch (error: any) {
            console.error('Error loading algo config:', error);
        } finally {
            setLoadingConfig(false);
        }
    };

    const executeTrade = async () => {
        if (!selectedInstance || !selectedLinkedAlgo) return;

        try {
            setLoading(true);
            setResult(null);
            const response = await axios.post(`${API_URL}/test-trade/execute`, {
                instance_id: selectedInstance.id,
                linked_algo_id: selectedLinkedAlgo.id,
                action,
                symbol: selectedSymbol || undefined,
                override_quantity: overrideQuantity ? parseFloat(overrideQuantity) : undefined,
            });
            setResult(response.data);
        } catch (error: any) {
            setResult({
                success: false,
                message: 'Request failed',
                error: error.response?.data?.detail || error.message,
                debug_log: error.response?.data?.debug_log || [],
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInstanceChange = (instanceId: number) => {
        const instance = instances.find((i) => i.id === instanceId);
        setSelectedInstance(instance || null);
        setSelectedLinkedAlgo(null);
        setAlgoConfig(null);
        setSelectedSymbol('');
        setResult(null);
    };

    const handleLinkedAlgoChange = (algoId: number) => {
        const algo = selectedInstance?.linked_algos.find((a) => a.id === algoId);
        setSelectedLinkedAlgo(algo || null);
        setResult(null);
    };

    const getBrokerBadgeColor = (brokerName: string) => {
        const n = brokerName?.toLowerCase() || '';
        if (n.includes('alpaca')) return 'primary';
        if (n.includes('ibkr') || n.includes('interactive')) return 'info';
        if (n.includes('tradelocker')) return 'success';
        if (n.includes('oanda')) return 'warning';
        return 'secondary';
    };

    const getStepIcon = (step: DebugStep) => {
        if (step.error) return <FaTimesCircle color="#e74c3c" />;
        if (step.label.toLowerCase().includes('success')) return <FaCheckCircle color="#2ecc71" />;
        if (step.label.toLowerCase().includes('reject') || step.label.toLowerCase().includes('fail'))
            return <FaExclamationTriangle color="#e67e22" />;
        return <FaClock color="#6c63ff" />;
    };

    const allLogs = result?.debug_log.find(s => s.label === 'Captured Broker Logs')?.data?.logs || [];

    return (
        <Container fluid className="py-4">
            <style>{`
                .tt-page { max-width: 1200px; margin: 0 auto; }
                .tt-header {
                    background: #11131a;
                    color: #e8eaed;
                    padding: 1.5rem 2rem;
                    border-bottom: 1px solid #252a38;
                }
                .tt-card {
                    background: #11131a;
                    border: 1px solid #252a38;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 1.5rem;
                }
                .tt-section { padding: 1.5rem 2rem; border-bottom: 1px solid #1a1e2a; }
                .tt-section:last-child { border-bottom: none; }
                .symbol-pill {
                    display: inline-block; padding: 0.4rem 1rem; margin: 0.25rem;
                    border-radius: 6px; font-weight: 500; cursor: pointer;
                    background: #181b24; color: #8b919a; transition: all 0.2s;
                    border: 1px solid #252a38; font-size: 0.8125rem;
                }
                .symbol-pill:hover { background: #1e2230; color: #e8eaed; }
                .symbol-pill.selected {
                    background: #1a2e22;
                    color: #4ade80;
                    border-color: #2d4a35;
                }
                .btn-exec {
                    padding: 0.6rem 1.5rem; font-size: 0.875rem; font-weight: 600;
                    border-radius: 6px; border: 1px solid #252a38; transition: all 0.2s;
                }
                .btn-exec:hover { transform: translateY(-1px); }
                .btn-exec.buy { background: #1a2e22; color: #4ade80; border-color: #2d4a35; }
                .btn-exec.sell { background: #2e1a1a; color: #f87171; border-color: #4a2d2d; }

                .debug-panel {
                    background: #11131a;
                    border: 1px solid #252a38;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 2rem;
                }
                .debug-header {
                    padding: 1rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
                    display: flex; align-items: center; gap: 0.5rem;
                }
                .debug-header.success { background: #1a2e22; color: #4ade80; border-bottom: 1px solid #2d4a35; }
                .debug-header.error { background: #2e1a1a; color: #f87171; border-bottom: 1px solid #4a2d2d; }
                .step-row {
                    display: flex; align-items: flex-start; gap: 0.75rem;
                    padding: 0.75rem 1.5rem; border-bottom: 1px solid #1a1e2a;
                    transition: background 0.15s;
                }
                .step-row:hover { background: #181b24; }
                .step-num {
                    min-width: 28px; height: 28px; border-radius: 6px;
                    background: #2a3550; color: #6b9fff; display: flex;
                    align-items: center; justify-content: center;
                    font-size: 0.75rem; font-weight: 700; flex-shrink: 0;
                }
                .step-num.err { background: #2e1a1a; color: #f87171; }
                .step-num.ok { background: #1a2e22; color: #4ade80; }
                .step-body { flex: 1; min-width: 0; }
                .step-label { font-weight: 600; font-size: 0.875rem; color: #e8eaed; }
                .step-fn { font-family: 'JetBrains Mono', monospace; font-size: 0.75rem; color: #8b919a; }
                .step-ts { font-size: 0.75rem; color: #5c6370; white-space: nowrap; }
                .step-error {
                    background: #2e1a1a; border-left: 2px solid #f87171;
                    padding: 0.5rem 0.75rem; border-radius: 4px; margin-top: 0.4rem;
                    font-family: 'JetBrains Mono', monospace; font-size: 0.75rem; color: #f87171;
                    white-space: pre-wrap; word-break: break-word;
                }

                .log-lines {
                    background: #090a0f; color: #8b919a; padding: 1rem;
                    border-radius: 6px; font-family: 'JetBrains Mono', monospace;
                    font-size: 0.75rem; max-height: 400px; overflow: auto;
                    margin: 0.5rem 0; border: 1px solid #1a1e2a;
                }
                .log-lines .warn { color: #fbbf24; font-weight: 600; }
                .log-lines .err { color: #f87171; font-weight: 700; }
                .log-lines .info { color: #6b9fff; }
                .log-lines .calc { color: #4ade80; font-weight: 600; }

                .trace-card {
                    background: #181b24; border: 1px solid #252a38;
                    border-radius: 8px; padding: 1.25rem; margin: 1rem;
                }
                .trace-val { font-family: 'JetBrains Mono', monospace; font-weight: 600; color: #6b9fff; }
                .trace-val.empty { color: #5c6370; font-weight: 400; }
            `}</style>

            <div className="tt-page">
                {/* ─── Config Card ──────────────────────────────────── */}
                <div className="tt-card">
                    <div className="tt-header">
                        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <FaBug size={28} /> Test Trade Debugger
                        </h2>
                        <p style={{ margin: '0.4rem 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                            Execute trades via the real broker pipeline and inspect every step
                        </p>
                    </div>

                    <div className="tt-section">
                        <div style={{ fontWeight: 600, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <FaDatabase /> Select Instance
                        </div>
                        <Form.Select
                            value={selectedInstance?.id || ''}
                            onChange={(e) => handleInstanceChange(Number(e.target.value))}
                        >
                            <option value="">-- Select Instance --</option>
                            {instances.map((inst) => (
                                <option key={inst.id} value={inst.id}>
                                    {inst.name} ({inst.broker?.name}) — {inst.status}
                                </option>
                            ))}
                        </Form.Select>
                        {selectedInstance && (
                            <div className="mt-2 d-flex gap-2 flex-wrap">
                                <Badge bg={getBrokerBadgeColor(selectedInstance.broker?.name)}>
                                    {selectedInstance.broker?.name}
                                </Badge>
                                <Badge bg="secondary">{selectedInstance.broker_account?.account_id}</Badge>
                                <Badge bg={selectedInstance.broker_account?.account_type === 'paper' ? 'warning' : 'success'}>
                                    {selectedInstance.broker_account?.account_type?.toUpperCase()}
                                </Badge>
                            </div>
                        )}
                    </div>

                    {selectedInstance && (
                        <div className="tt-section">
                            <div style={{ fontWeight: 600, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <FaCog /> Select Algorithm
                            </div>
                            <Form.Select
                                value={selectedLinkedAlgo?.id || ''}
                                onChange={(e) => handleLinkedAlgoChange(Number(e.target.value))}
                            >
                                <option value="">-- Select Algorithm --</option>
                                {selectedInstance.linked_algos.map((a) => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    {!loadingConfig && algoConfig?.symbols?.length > 0 && (
                        <div className="tt-section">
                            <div style={{ fontWeight: 600, marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <FaChartLine /> Symbol
                            </div>
                            <div>
                                {algoConfig.symbols.map((s) => (
                                    <span
                                        key={s.symbol}
                                        className={`symbol-pill ${selectedSymbol === s.symbol ? 'selected' : ''}`}
                                        onClick={() => setSelectedSymbol(s.symbol)}
                                    >
                                        {s.symbol} <small>({s.assetClass})</small>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {algoConfig && (
                        <div className="tt-section">
                            <Row className="align-items-end">
                                <Col md={4}>
                                    <Form.Label style={{ fontWeight: 600 }}>Direction</Form.Label>
                                    <ButtonGroup className="w-100">
                                        <Button
                                            variant={action === 'BUY' ? 'success' : 'outline-success'}
                                            onClick={() => setAction('BUY')}
                                        >BUY</Button>
                                        <Button
                                            variant={action === 'SELL' ? 'danger' : 'outline-danger'}
                                            onClick={() => setAction('SELL')}
                                        >SELL</Button>
                                    </ButtonGroup>
                                </Col>
                                <Col md={4}>
                                    <Form.Label style={{ fontWeight: 600 }}>Override Qty <small className="text-muted">(optional)</small></Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="Auto from risk params"
                                        value={overrideQuantity}
                                        onChange={(e) => setOverrideQuantity(e.target.value)}
                                    />
                                </Col>
                                <Col md={4} className="d-flex align-items-end">
                                    <Button
                                        className={`btn-exec ${action === 'BUY' ? 'buy' : 'sell'} w-100`}
                                        onClick={executeTrade}
                                        disabled={loading || !selectedSymbol}
                                    >
                                        {loading ? (
                                            <><Spinner animation="border" size="sm" className="me-2" /> Executing...</>
                                        ) : (
                                            <><FaPlay className="me-2" /> Execute {action}</>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}

                    <div className="tt-section" style={{ background: '#181b24' }}>
                        <Alert variant="warning" className="mb-0 py-2">
                            <strong>⚠️</strong> This executes a <strong>real trade</strong>. Use a paper account for testing!
                        </Alert>
                    </div>
                </div>

                {/* ─── Debug Summary Panel ───────────────────────────── */}
                {result && (
                    <div className="debug-panel">
                        <div className={`debug-header ${result.success ? 'success' : 'error'}`}>
                            {result.success ? <FaCheckCircle size={22} /> : <FaTimesCircle size={22} />}
                            {result.message}
                        </div>

                        <Tabs defaultActiveKey="summary" className="px-3 mt-2">
                            <Tab eventKey="summary" title={<span><FaListUl className="me-1" /> Summary</span>}>
                                {/* 1. Quantity Calculation Breakdown */}
                                {result.quantity_trace && (
                                    <div className="trace-card">
                                        <div style={{ fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e8eaed' }}>
                                            <FaCalculator color="#6c63ff" /> Quantity Calculation Trace
                                        </div>
                                        <Table responsive bordered size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Parameter</th>
                                                    <th>Value</th>
                                                    <th>Parameter</th>
                                                    <th>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Calc Function</td><td className="trace-val" colSpan={3} style={{ color: '#667eea' }}>{result.quantity_trace.function || '—'}</td>
                                                </tr>
                                                <tr>
                                                    <td>Lot Mode</td><td className="trace-val">{result.quantity_trace.lot_mode || 'N/A'}</td>
                                                    <td>Balance</td><td className={`trace-val ${!result.quantity_trace.balance ? 'empty' : ''}`}>{result.quantity_trace.balance || '—'}</td>
                                                </tr>
                                                <tr>
                                                    <td>Risk %</td><td className={`trace-val ${!result.quantity_trace.risk_percentage ? 'empty' : ''}`}>{result.quantity_trace.risk_percentage ? result.quantity_trace.risk_percentage + '%' : '—'}</td>
                                                    <td>Money at Risk</td><td className={`trace-val ${!result.quantity_trace.money_at_risk ? 'empty' : ''}`}>{result.quantity_trace.money_at_risk ? '$' + result.quantity_trace.money_at_risk : '—'}</td>
                                                </tr>
                                                <tr>
                                                    <td>Point Size</td><td className={`trace-val ${!result.quantity_trace.point_size ? 'empty' : ''}`}>{result.quantity_trace.point_size || '—'}</td>
                                                    <td>Point Value</td><td className={`trace-val ${!result.quantity_trace.point_value ? 'empty' : ''}`}>{result.quantity_trace.point_value || '—'}</td>
                                                </tr>
                                                {!result.quantity_trace.is_fixed && (
                                                    <>
                                                        <tr>
                                                            <td>SL Distance</td><td className="trace-val">{result.quantity_trace.sl_distance || '—'}</td>
                                                            <td>Risk Points</td><td className="trace-val">{result.quantity_trace.risk_points || '—'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Denominator</td><td className="trace-val">{result.quantity_trace.denominator || '—'}</td>
                                                            <td><strong>Calc Lots</strong></td><td className="trace-val" style={{ color: '#2ecc71', fontSize: '1rem' }}>{result.quantity_trace.calculated_lots || '—'}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                {result.quantity_trace.is_fixed && (
                                                    <tr>
                                                        <td colSpan={2}>Mode is Fixed</td>
                                                        <td><strong>Final Lots</strong></td><td className="trace-val" style={{ color: '#6c63ff', fontSize: '1rem' }}>{result.quantity_trace.calculated_lots}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                        {!result.quantity_trace.is_fixed && (
                                            <small className="text-muted">Lots = moneyAtRisk / (pointValue * riskPoints)</small>
                                        )}
                                    </div>
                                )}

                                {/* 2. Database Persistence */}
                                {result.saved_trade && (
                                    <div className="trace-card" style={{ borderLeft: '4px solid #2ecc71' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#155724' }}>
                                            <FaSave /> Database Record Created
                                        </div>
                                        <Row>
                                            <Col md={3}><strong>Trade ID:</strong> <span className="trace-val">#{result.saved_trade.id}</span></Col>
                                            <Col md={3}><strong>Symbol:</strong> <span className="trace-val">{result.saved_trade.symbol}</span></Col>
                                            <Col md={3}><strong>Quantity:</strong> <span className="trace-val">{result.saved_trade.quantity}</span></Col>
                                            <Col md={3}><strong>Side:</strong> <Badge bg={result.saved_trade.side === 'BUY' ? 'success' : 'danger'}>{result.saved_trade.side}</Badge></Col>
                                        </Row>
                                        <Row className="mt-2">
                                            <Col md={6}><strong>Order ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{result.saved_trade.order_id}</span></Col>
                                            <Col md={3}><strong>Status:</strong> <Badge bg="info">{result.saved_trade.status}</Badge></Col>
                                            <Col md={3} className="text-end">
                                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>{new Date(result.saved_trade.date).toLocaleString()}</span>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                {/* Error summary */}
                                {result.error && !result.success && (
                                    <div className="px-3 pb-3">
                                        <Alert variant="danger" className="mb-0">
                                            <strong>Error Detail:</strong>
                                            <pre style={{ margin: '0.5rem 0 0', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{result.error}</pre>
                                        </Alert>
                                    </div>
                                )}
                            </Tab>

                            <Tab eventKey="logs" title={<span><FaTerminal className="me-1" /> Raw Logs</span>}>
                                <div className="px-3 py-3">
                                    <div className="log-lines">
                                        {allLogs.length > 0 ? allLogs.map((line, i) => {
                                            let cls = 'info';
                                            if (line.includes('WARNING')) cls = 'warn';
                                            else if (line.includes('ERROR')) cls = 'err';
                                            else if (line.includes('lot') || line.includes('Risk') || line.includes('Balance') || line.includes('Point')) cls = 'calc';

                                            return <div key={i} className={cls} style={{ marginBottom: '2px' }}>{line}</div>;
                                        }) : <div className="text-muted">No broker logs captured.</div>}
                                    </div>
                                </div>
                            </Tab>

                            <Tab eventKey="timeline" title={<span><FaHistory className="me-1" /> Timeline</span>}>
                                <div className="py-2">
                                    {result.debug_log.map((step) => {
                                        const isError = !!step.error;
                                        const isSuccess = step.label.toLowerCase().includes('success');
                                        const isBrokerLog = step.label === 'Captured Broker Logs';
                                        if (isBrokerLog) return null; // Skip full logs here to keep it clean

                                        return (
                                            <div key={step.step}>
                                                <div className="step-row">
                                                    <div className={`step-num ${isError ? 'err' : isSuccess ? 'ok' : ''}`}>{step.step}</div>
                                                    <div className="step-body">
                                                        <div className="d-flex align-items-center gap-2">
                                                            {getStepIcon(step)}
                                                            <span className="step-label">{step.label}</span>
                                                        </div>
                                                        <div className="step-fn">{step.function}</div>
                                                        {step.error && <div className="step-error">{step.error}</div>}
                                                        {step.data && <JsonBlock data={step.data} />}
                                                    </div>
                                                    <span className="step-ts">{new Date(step.timestamp).toLocaleTimeString()}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Tab>

                            <Tab eventKey="broker" title={<span><FaExchangeAlt className="me-1" /> Broker Env</span>}>
                                <div className="px-3 py-3">
                                    <JsonBlock label="Full Broker Response" data={result.broker_response} defaultOpen />
                                    <JsonBlock label="Trade Details" data={result.trade_details} />
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </div>
        </Container>
    );
};

// Add missing icon
const FaExchangeAlt = (props: any) => <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" {...props}><path d="M0 168v-16c0-13.255 10.745-24 24-24h360V80c0-21.367 25.899-32.042 40.971-16.971l80 80c9.372 9.373 9.372 24.569 0 33.941l-80 80c-15.071 15.071-40.971 4.403-40.971-16.971v-48H24c-13.255 0-24-10.745-24-24zm488 152H128v-48c0-21.314-25.862-32.08-40.971-16.971l-80 80c-9.372 9.373-9.372 24.569 0 33.941l80 80c15.099 15.099 40.971 4.35 40.971-16.971V408h360c13.255 0 24-10.745 24-24v-16c0-13.255-10.745-24-24-24z"></path></svg>;
const FaHistory = (props: any) => <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" {...props}><path d="M504 255.531c.253 136.64-111.18 248.372-247.82 248.468-59.015.042-113.223-20.53-155.822-54.911-11.077-8.94-11.905-25.541-1.839-35.607l11.267-11.267c8.609-8.609 22.353-9.551 31.891-1.984C173.062 425.135 212.781 440 256 440c101.705 0 184-82.311 184-184 0-101.705-82.311-184-184-184-48.814 0-93.192 18.991-126.068 49.932l45.441 45.441C186.205 178.205 178.367 192 163.03 192H48c-13.255 0-24-10.745-24-24V52.971c0-15.337 13.795-23.175 25.171-11.799l44.381 44.381C137.391 35.32 192.83 8 256 8c136.811 0 247.747 110.78 248 247.531zm-180.912 78.784l9.823-12.63c8.114-10.432 6.64-25.371-3.376-33.911L280 244.402V128c0-13.255-10.745-24-24-24h-16c-13.255 0-24 10.745-24 24v131.598c0 8.191 4.162 15.849 10.971 20.255l58.109 37.545c11.393 7.361 26.68 4.414 34.008-6.913z"></path></svg>;

export default TestTrade;
